import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import AvailableTime from "../components/AvailableTime";
import InputWrapper from "../components/InputWrapper";
import Empty from "../components/Empty";
import {useContext, useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import Data from "../Data";
import ukMoment from "../../../utils/ukTime";
import AppContext from "../../../context";
import {X} from "react-feather";

const EXPERIENCE_CART_KEY = 'experience-cart';

const readCart = (key) => {
    try {
        const cart = JSON.parse(localStorage.getItem(key) || '[]');
        return Array.isArray(cart) ? cart : [];
    } catch (error) {
        return [];
    }
};

const mergeCartItems = (items) => Object.values(items.reduce((cart, item) => ({
    ...cart,
    [item.eventId]: item
}), {}));

const Experience = ({ basePath = '' }) => {
    const context = useContext(AppContext);
    const { id } = useParams();
    if(!id) window.location.href = "/"

    const [experience, setExperience] = useState(null);
    const [date, setDate] = useState(null);
    const [tickets, setTickets] = useState(1);
    const [event, setEvent] = useState(null);
    const [cart, setCart] = useState([]);
    const [waitlistLoading, setWaitlistLoading] = useState(null);
    const [waitlistMessage, setWaitlistMessage] = useState("");
    const [waitlistError, setWaitlistError] = useState("");

    useEffect(async () => {
        try {
            const experience = await Data.fetchExperienceById( { id})
            setExperience(experience.data)
            const legacyCartKey = `experience-cart-${id}`;
            const storedCart = mergeCartItems([
                ...readCart(EXPERIENCE_CART_KEY),
                ...readCart(legacyCartKey)
            ]);
            localStorage.setItem(EXPERIENCE_CART_KEY, JSON.stringify(storedCart));
            localStorage.removeItem(legacyCartKey);
            setCart(storedCart);
        } catch (error) {
            window.location.href = "/"
        }
    }, []);

    if(!experience) return null

    const availableDates = experience.futureEvents.map(event => {
        return new Date(event.date).setHours(0, 0, 0, 0)
    })
    const isDisabled = (date) => {
        date = date.setHours(0, 0, 0, 0);
        return availableDates.indexOf(date) === -1;
    }

    const rawTicketsRemaining = experience.futureEvents.find(availableEvent => event === availableEvent._id)?.ticketsRemaining ?? 0
    const ticketsRemaining = Math.max(0, rawTicketsRemaining)
    const selectedDate = experience.futureEvents.filter(event => new Date(event.date).setHours(0, 0, 0, 0) === new Date(date).setHours(0, 0, 0, 0))
    const selectedEvent = experience.futureEvents.find(availableEvent => event === availableEvent._id);

    const addToCart = () => {
        if(!event || ticketsRemaining < tickets) return;
        setCart((current) => {
            const next = current.filter(item => item.eventId !== event);
            const updated = [...next, {
                eventId: event,
                tickets: Number(tickets),
                experienceId: id,
                experienceName: experience.name,
                price: Number(experience.price || 0),
                label: `${ukMoment(selectedEvent.date).format("MMM Do, YYYY")} at ${ukMoment(selectedEvent.date).format("HH:mm A")}`
            }];
            localStorage.setItem(EXPERIENCE_CART_KEY, JSON.stringify(updated));
            return updated;
        });
    }

    const removeFromCart = (eventId) => {
        setCart((current) => {
            const updated = current.filter(item => item.eventId !== eventId);
            localStorage.setItem(EXPERIENCE_CART_KEY, JSON.stringify(updated));
            return updated;
        });
    }

    const checkoutCart = () => {
        window.location.href = `${basePath}/${id}/checkout/${context.user ? 'confirm' : 'create-account'}?bulk=true`;
    }

    const toggleWaitlist = async (eventId) => {
        const selected = experience.futureEvents.find(item => item._id === eventId);
        if(!selected) return;

        setWaitlistLoading(eventId);
        setWaitlistMessage("");
        setWaitlistError("");

        try {
            if(selected.waitlisted) {
                await Data.leaveWaitlist({ type: "event", eventId });
                setExperience(current => ({
                    ...current,
                    futureEvents: current.futureEvents.map(item => item._id === eventId ? { ...item, waitlisted: false } : item)
                }));
                setWaitlistMessage("You've left the waitlist.");
            } else {
                await Data.joinWaitlist({ type: "event", eventId });
                setExperience(current => ({
                    ...current,
                    futureEvents: current.futureEvents.map(item => item._id === eventId ? { ...item, waitlisted: true } : item)
                }));
                setWaitlistMessage("You're on the waitlist. We'll email you if this time becomes available.");
            }
        } catch(error) {
            setWaitlistError(error.response?.data?.message || "Couldn't update the waitlist.");
        } finally {
            setWaitlistLoading(null);
        }
    }

    return (
        <div className={"p-8"}>
            <div className={"container mx-auto grid grid-cols-1 gap-4 lg:grid-cols-2"}>
                <div className={"grid grid-cols-1 gap-4"}>
                    <InputWrapper label={"Date"} input={
                        <DateCalendar
                            value={date}
                            shouldDisableDate={isDisabled}
                            onChange={e => {
                                setDate(e)
                                setEvent(null)
                            }}
                        />
                    }/>
                </div>

                <div className={"flex flex-col gap-4"}>
                    {date ? <div className={"grid grid-cols-1 gap-4"}>
                        <p>Available Times</p>
                        {waitlistMessage ? <p className={"text-primary text-sm"}>{waitlistMessage}</p> : null}
                        {waitlistError ? <p className={"text-red-500 text-sm"}>{waitlistError}</p> : null}

                        <div className={"grid grid-cols-1 lg:grid-cols-3 gap-4"}>
                            {selectedDate.length > 0 ? selectedDate.map(availableEvent => (
                                <AvailableTime
                                    key={availableEvent._id}
                                    _id={availableEvent._id}
                                    ticketsRemaining={availableEvent.ticketsRemaining}
                                    event={event}
                                    setEvent={setEvent}
                                    time={ukMoment(availableEvent.date).format("HH:mm A")}
                                    inCart={cart.some(item => item.eventId === availableEvent._id)}
                                    waitlisted={availableEvent.waitlisted}
                                    onToggleWaitlist={toggleWaitlist}
                                    waitlistLoading={waitlistLoading === availableEvent._id}
                                    isAuthenticated={!!context.user}
                                />
                            )) : <Empty>There aren't any times available for this day.</Empty>}
                        </div>
                    </div> : null}

                    {date && event ?
                        ticketsRemaining > 0 ?
                        <>
                            <InputWrapper label={"Tickets"} input={
                                <select value={tickets} onChange={e => setTickets(e.target.value)}>
                                    {[...Array(ticketsRemaining).keys()].map(i => (
                                        <option key={i + 1} value={i + 1}>
                                            {i + 1} ticket{i + 1 > 1 ? 's' : ''}
                                        </option>
                                    ))}
                                </select>
                            }/>

                            <div>
                                <button type="button"
                                      onClick={addToCart}
                                      className={"inline-block bg-primary py-2 px-4 w-full lg:w-auto text-white text-center rounded-md border-0"}>Add to Cart</button>
                            </div>
                        </>
                        : <Empty>This time is sold out.</Empty>
                    : null }

                    {cart.length ? (
                        <div className={"border rounded-lg p-6 grid gap-3"}>
                            <h2>Cart</h2>
                            {cart.map(item => (
                                <div key={item.eventId} className={"border rounded-md p-3 flex items-center justify-between gap-3"}>
                                    <div>
                                        <p className={"font-semibold"}>{item.experienceName || experience.name}</p>
                                        <p>{item.label}</p>
                                        <p className={"text-sm text-textGrey"}>{item.tickets} ticket{item.tickets > 1 ? 's' : ''}</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeFromCart(item.eventId)}
                                        aria-label={`Remove ${item.label} from cart`}
                                        className={"border-0 bg-backgroundGrey p-2 rounded-md hover:bg-red-100 hover:text-red-700 transition"}
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                            ))}
                            <button type="button" onClick={checkoutCart} className={"inline-block bg-primary py-2 px-4 w-full lg:w-auto text-white text-center rounded-md border-0"}>
                                Continue
                            </button>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    )
}

export default Experience
